import React, { lazy, Suspense } from "react";
import { Await } from "react-router";
import CustomError from "@components/Error/CustomError";
import Spinner from "@components/Spinner/Spinner";
import { ArticlePageContext } from "@pages/Article/utils/types";
import { TRouteWithRedirect, useRouteContext } from "@sphtech/web2-core/ssr";
import { HttpStatusCode } from "axios";

import { StaticPageContext } from "./Static/utils/types";
import { StoryThreadContext } from "./StoryThread/utilities/types";

const ArticleWrapper = lazy(() => import("@pages/Article/ArticleWrapper"));
const StoryThreadPage = lazy(
  () => import("@pages/StoryThread/StoryThreadPage")
);
const StaticPage = lazy(() => import("@pages/Static/StaticPage"));

export default function DefaultPageHandler(): React.ReactElement {
  const { context } =
    useRouteContext<
      TRouteWithRedirect<
        ArticlePageContext | StoryThreadContext | StaticPageContext,
        string
      >
    >();

  // StatusCode is not 200
  if (context && context.statusCode !== HttpStatusCode.Ok) {
    return <CustomError statusCode={context.statusCode} />;
  }

  return (
    <Await resolve={context.payload} errorElement={<CustomError />}>
      {(
        context: ArticlePageContext | StoryThreadContext | StaticPageContext
      ) => {
        const { kind, data } = context;

        // Load article page content.
        if (kind === "art") {
          return (
            <Suspense fallback={<Spinner />}>
              <ArticleWrapper data={data} />
            </Suspense>
          );
        }

        // Load storythread page content.
        if (kind === "storythread") {
          const { entity } = context;

          return (
            <Suspense fallback={<Spinner />}>
              <StoryThreadPage {...{ data, entity }} />
            </Suspense>
          );
        }

        // Load static page content.
        if (kind === "static") {
          return (
            <Suspense fallback={<Spinner />}>
              <StaticPage {...{ data }} />
            </Suspense>
          );
        }

        // Default error page.
        return <CustomError statusCode={HttpStatusCode.NotFound} />;
      }}
    </Await>
  );
}
